import { useUsageQuery } from './api.js';
import { resetText, usageGroups, type UsageLine } from './usageModel.js';
import { Alert, Card, SectionHeading, Spinner } from './ui.js';

/**
 * Settings → Usage & Limits. Read-only: nothing here changes a limit.
 *
 * Every number comes from the server's own count. The page adds wording, never arithmetic
 * beyond "limit minus used", and that is the same subtraction the server performs.
 */
function Meter({ percent, exhausted }: { percent: number; exhausted: boolean }) {
  return (
    <div
      className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100"
      role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}
    >
      <div
        className={`h-full rounded-full ${exhausted ? 'bg-amber-500' : 'bg-slate-700'}`}
        style={{ width: `${percent}%` }}
      />
    </div>
  );
}

function Line({ line }: { line: UsageLine }) {
  return (
    <li className="p-4" data-usage-line={line.key}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h3 className="text-sm font-medium text-slate-900">{line.title}</h3>
        <p className="text-sm tabular-nums text-slate-700" data-usage-headline>{line.headline}</p>
      </div>
      {line.percent !== null ? <Meter percent={line.percent} exhausted={line.exhausted} /> : null}
      <div className="mt-2 flex flex-wrap justify-between gap-x-4 gap-y-1 text-xs text-slate-500">
        {line.remaining ? (
          <span className={line.exhausted ? 'font-medium text-amber-700' : ''} data-usage-remaining>{line.remaining}</span>
        ) : <span />}
        {line.resetsAt ? <span>{resetText(line.resetsAt)}</span> : null}
      </div>
      {line.notes.map((note) => (
        <p key={note} className="mt-1 text-xs text-slate-500">{note}</p>
      ))}
    </li>
  );
}

export function UsageLimits() {
  const { data, isLoading, isError } = useUsageQuery(undefined, { pollingInterval: 60_000 });

  if (isLoading) return <Spinner />;
  if (isError || !data) return <Alert tone="error">Usage could not be loaded. Try again in a moment.</Alert>;

  const groups = usageGroups(data);
  return (
    <div className="space-y-6">
      <section>
        <SectionHeading
          title="Usage & Limits"
          hint="How much of each allowance is used, and when it resets. Daily limits reset at midnight UTC, shown here in your own time."
        />
        {data.paused ? (
          <Alert tone="error">New capture and new Keap changes are paused for this workspace. Work already sent is not affected.</Alert>
        ) : (
          <p className="text-sm text-slate-700" data-usage-status>
            <span className="font-medium text-slate-900">Processing is on.</span>{' '}
            New business cards, emails and CSV imports are being accepted.
          </p>
        )}
        {data.restricted ? (
          <div className="mt-2">
            <Alert tone="info">A temporary safety limit is lower than your plan on at least one item below.</Alert>
          </div>
        ) : null}
      </section>

      {groups.map((g) => (
        <section key={g.key} data-usage-group={g.key}>
          <SectionHeading title={g.title} hint={g.hint} />
          <Card>
            <ul className="divide-y divide-slate-100">
              {g.lines.map((l) => <Line key={l.key} line={l} />)}
            </ul>
          </Card>
        </section>
      ))}

      <section>
        <SectionHeading title="Waiting work" hint="Approved contacts are never dropped when a limit is reached. They wait and are sent when there is room." />
        <Card className="p-4">
          <dl className="grid gap-3 sm:grid-cols-2">
            <div>
              <dt className="text-sm text-slate-500">Waiting for tomorrow's CRM capacity</dt>
              <dd className="text-2xl font-semibold tabular-nums text-slate-900" data-usage-waiting>
                {data.pending.waitingForCapacity.toLocaleString('en-US')}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-slate-500">Approved and being sent</dt>
              <dd className="text-2xl font-semibold tabular-nums text-slate-900">
                {data.pending.approvedInFlight.toLocaleString('en-US')}
              </dd>
            </div>
          </dl>
        </Card>
      </section>
    </div>
  );
}
